import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTiptap, useTiptapState } from "@tiptap/react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBold, faItalic, faLink, faStrikethrough, faUnderline, faPalette, faListUl, faListOl, faHorizontalRule, faAlignLeft, faLineHeight, faTextSize } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks/redux";
import { TWebsiteBuilderRichTextResponsiveFontSize } from "../extensions/websiteBuilderRichTextResponsiveFontSize";
import { WebsiteBuilderRichTextColorPicker } from "./WebsiteBuilderRichTextColorPicker";
import { WebsiteBuilderRichTextSelect } from "./WebsiteBuilderRichTextSelect";
import { TWebsiteBuilderRichTextMenuPlacement } from "./WebsiteBuilderRichTextMenu";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import { TWebsiteBuilderLinkDestination, TWebsiteBuilderLinkDestinationType } from "../../nodes/element/websiteBuilderLink";
import "./websiteBuilderParagraphRichTextMenu.scss";

interface IWebsiteBuilderParagraphRichTextMenuProps {
  elementId: string;
  menuPlacement: TWebsiteBuilderRichTextMenuPlacement;
}

type TWebsiteBuilderParagraphRichTextMenuOptionKey =
  | "bold"
  | "italic"
  | "strike"
  | "underline"
  | "color"
  | "bullet-list"
  | "ordered-list"
  | "horizontal-rule"
  | "link"
  | "line-height"
  | "text-align"
  | "font-size";

interface IWebsiteBuilderParagraphRichTextMenuOption {
  key: TWebsiteBuilderParagraphRichTextMenuOptionKey;
  label: string;
  icon: IconDefinition;
  selected: boolean;
  onClick?: () => void;
}

const PARAGRAPH_FONT_SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "40px", "48px"];
const PARAGRAPH_LINE_HEIGHT_OPTIONS = ["1", "1.25", "1.5", "1.75", "2"];

export function WebsiteBuilderParagraphRichTextMenu(props: IWebsiteBuilderParagraphRichTextMenuProps) {
  const { elementId, menuPlacement } = props;

  const { t } = useTranslation();

  const { editor } = useTiptap();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const colorButtonRef = useRef<HTMLButtonElement>(null);
  
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [linkSheetOpen, setLinkSheetOpen] = useState(false);

  const closeLinkSheet = useCallback(() => {
    setLinkSheetOpen(false);
  }, []);

  const editorState = useTiptapState(({ editor }) => {
    const responsiveFontSize: TWebsiteBuilderRichTextResponsiveFontSize = editor.state.doc.attrs.responsiveFontSize;
    const lineHeight: unknown = editor.getAttributes("paragraph").lineHeight;
    const textAlign: unknown = editor.getAttributes("paragraph").textAlign;

    return {
      responsiveFontSize,
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      strike: editor.isActive("strike"),
      underline: editor.isActive("underline"),
      bulletList: editor.isActive("bulletList"),
      orderedList: editor.isActive("orderedList"),
      link: editor.isActive("link"),
      lineHeight: typeof lineHeight === "string" ? lineHeight : null,
      textAlign: typeof textAlign === "string" ? textAlign : null,
    };
  });

  const fontSize = editorState.responsiveFontSize[deviceView];

  function updateFontSize(value: string) {
    if (value === "") {
      editor.chain().focus().unsetResponsiveFontSize(deviceView).run();
    } else {
      editor.chain().focus().setResponsiveFontSize(deviceView, value).run();
    }
  }

  function updateLineHeight(lineHeight: string) {
    if (lineHeight === "") {
      editor.chain().focus().resetAttributes("paragraph", "lineHeight").run();
    } else {
      editor.chain().focus().updateAttributes("paragraph", { lineHeight }).run();
    }
  }

  function updateTextAlign(textAlign: string) {
    if (textAlign === "") {
      editor.chain().focus().unsetTextAlign().run();
    } else {
      editor.chain().focus().setTextAlign(textAlign).run();
    }
  }

  const options: IWebsiteBuilderParagraphRichTextMenuOption[] = [
    {
      key: "bold",
      label: t("Bold"),
      icon: faBold,
      selected: editorState.bold,
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    {
      key: "italic",
      label: t("Italic"),
      icon: faItalic,
      selected: editorState.italic,
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: "strike",
      label: t("Strikethrough"),
      icon: faStrikethrough,
      selected: editorState.strike,
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      key: "underline",
      label: t("Underline"),
      icon: faUnderline,
      selected: editorState.underline,
      onClick: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      key: "color",
      label: t("Color"),
      icon: faPalette,
      selected: colorPickerOpen,
      onClick: () => setColorPickerOpen(prev => !prev),
    },
    {
      key: "bullet-list",
      label: t("Bullet List"),
      icon: faListUl,
      selected: editorState.bulletList,
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      key: "ordered-list",
      label: t("Numbered List"),
      icon: faListOl,
      selected: editorState.orderedList,
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: "horizontal-rule",
      label: t("Horizontal line"),
      icon: faHorizontalRule,
      selected: false,
      onClick: () => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      key: "link",
      label: t("Link"),
      icon: faLink,
      selected: editorState.link || linkSheetOpen,
      onClick: () => setLinkSheetOpen(prev => !prev),
    },
    {
      key: "font-size",
      label: t("Font Size"),
      icon: faTextSize,
      selected: fontSize != null,
    },
    {
      key: "line-height",
      label: t("Line Height"),
      icon: faLineHeight,
      selected: editorState.lineHeight != null,
    },
    {
      key: "text-align",
      label: t("Text Align"),
      icon: faAlignLeft,
      selected: editorState.textAlign != null,
    },
  ];

  return (
    <>
      {options.map(option => (
        <OverlayTrigger
          key={option.key}
          placement={menuPlacement}
          delay={{
            show: 500,
            hide: 100,
          }}
          overlay={
            <Tooltip id={`website-builder-rich-text-menu-option-tooltip-${elementId}-${option.key}`}>
              {option.label}
            </Tooltip>
          }
        >
          <div
            className={classNames("website-builder-rich-text-menu-option", { selected: option.selected })}
          >
            {option.key === "font-size" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-font-size`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={fontSize ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  ...(fontSize != null && !PARAGRAPH_FONT_SIZE_OPTIONS.includes(fontSize)
                    ? [{ value: fontSize, label: fontSize }] : []),
                  ...PARAGRAPH_FONT_SIZE_OPTIONS.map(value => ({ value, label: value })),
                ]}
                onChange={updateFontSize}
              />
            ) : option.key === "line-height" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-line-height`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={editorState.lineHeight ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  ...(editorState.lineHeight != null && !PARAGRAPH_LINE_HEIGHT_OPTIONS.includes(editorState.lineHeight)
                    ? [{ value: editorState.lineHeight, label: editorState.lineHeight }] : []),
                  ...PARAGRAPH_LINE_HEIGHT_OPTIONS.map(value => ({ value, label: value })),
                ]}
                onChange={updateLineHeight}
              />
            ) : option.key === "text-align" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-text-align`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={editorState.textAlign ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  { value: "left", label: t("Left") },
                  { value: "center", label: t("Center") },
                  { value: "right", label: t("Right") },
                ]}
                onChange={updateTextAlign}
              />
            ) : (
              <button
                ref={option.key === "color" ? colorButtonRef : undefined}
                type="button"
                className="rich-text-menu-button"
                onClick={option.onClick}
              >
                <FontAwesomeIcon icon={option.icon} />
              </button>
            )}
          </div>
        </OverlayTrigger>
      ))}
      {colorPickerOpen && (
        <WebsiteBuilderRichTextColorPicker
          elementId={elementId}
          colorButtonRef={colorButtonRef}
          closeColorPicker={() => setColorPickerOpen(false)}
        />
      )}
      {linkSheetOpen && (
        <ParagraphRichTextLinkSheet closeLinkSheet={closeLinkSheet} />
      )}
    </>
  );
}

interface IParagraphRichTextLinkSheetProps {
  closeLinkSheet: () => void;
}

function ParagraphRichTextLinkSheet(props: IParagraphRichTextLinkSheetProps) {
  const { closeLinkSheet } = props;

  const { Option } = Select;

  const { t } = useTranslation();

  const { editor } = useTiptap();

  const selectionRef = useRef({ from: editor.state.selection.from, to: editor.state.selection.to });
  const existingLinkRef = useRef(editor.isActive("link"));

  const [destination, setDestination] = useState<TWebsiteBuilderLinkDestination>(() => {
    const attributes = editor.getAttributes("link");
    const href: unknown = attributes.href;
    const value = typeof href === "string" ? href : "";

    if (/^mailto:/i.test(value)) {
      const url = new URL(value);

      return {
        type: "email",
        address: decodeURIComponent(url.pathname),
        subject: url.searchParams.get("subject") ?? "",
      };
    } else {
      return {
        type: "url",
        href: value,
        blankTarget: attributes.target === "_blank",
      };
    }
  });

  const [hrefDirty, setHrefDirty] = useState(false);
  const [addressDirty, setAddressDirty] = useState(false);

  const hrefValid = destination.type === "url" && destination.href.trim() !== "";
  const addressValid = destination.type === "email" && destination.address.trim() !== "";

  const disabled = destination.type === "url" ? !hrefValid : !addressValid;

  function applyDestination() {
    if (disabled) {
      return;
    }

    let href: string;
    let text: string;

    let target = "_self";

    if (destination.type === "url") {
      href = destination.href.trim();
      text = href;
      href = /^www\./i.test(href) ? `https://${href}` : href;

      if (destination.blankTarget) {
        target = "_blank";
      }
    } else {
      const { subject, address } = destination;

      const params = new URLSearchParams();

      if (subject?.length > 0) {
        params.set("subject", subject);
      }

      const query = params.toString();
      href = `mailto:${address}${query.length > 0 ? `?${query}` : ""}`;
      text = address;
    }

    const chain = editor.chain().focus().setTextSelection(selectionRef.current).extendMarkRange("link").setLink({ href, target });
    
    if (selectionRef.current.from === selectionRef.current.to && !existingLinkRef.current) {
      chain.insertContent({ type: "text", text });
    }

    if (!chain.run()) {
      return;
    }

    closeLinkSheet();
  }

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeLinkSheet();
      }
    }

    document.addEventListener("keydown", handleKeydown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeydown, { capture: true });
  }, [closeLinkSheet]);

  return (
    <Portal parentId={WEBSITE_BUILDER_PORTAL}>
      <div
        onMouseOver={event => event.stopPropagation()}
        onMouseOut={event => event.stopPropagation()}
        onDoubleClick={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onContextMenu={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        style={{ zIndex: WEBSITE_BUILDER_Z_LAYERS.portal }}
      >
        <Sheet
          open={true}
          title={t("Link")}
          closable
          size="small"
          cancelText={t("Cancel")}
          okText={t("Apply")}
          okDisabled={disabled}
          onOk={applyDestination}
          onCancel={closeLinkSheet}
          backDropCancel={false}
        >
          <div className="website-builder-paragraph-rich-text-link-sheet-fields">
            <Select
              label={t("Destination Type")}
              defaultValue={destination.type}
              onChange={(type: TWebsiteBuilderLinkDestinationType) => {
                if (type !== destination.type) {
                  setDestination(type === "url"
                    ? { type: "url", href: "", blankTarget: false }
                    : { type: "email", address: "", subject: "" });
                  setHrefDirty(false);
                  setAddressDirty(false);
                }
              }}
              usePortal
              portalId={WEBSITE_BUILDER_PORTAL}
            >
              <Option value="url" name={t("Url")}>{t("Url")}</Option>
              <Option value="email" name={t("Email")}>{t("Email")}</Option>
            </Select>
            {destination.type === "url" ? (
              <>
                <Input
                  label={t("Link")}
                  value={destination.href}
                  prefix={<FontAwesomeIcon icon={faLink} />}
                  autoFocus
                  error={hrefDirty && !hrefValid}
                  onChange={event => {
                    setDestination({ ...destination, href: event.target.value });
                    setHrefDirty(true);
                  }}
                />
                <Checkbox
                  size="medium"
                  label={t("Open link in new tab")}
                  checked={destination.blankTarget}
                  onChange={event => setDestination({ ...destination, blankTarget: event.target.checked })}
                />
              </>
            ) : (
              <>
                <Input
                  label={t("Address")}
                  value={destination.address}
                  prefix={<FontAwesomeIcon icon={faLink} />}
                  autoFocus
                  error={addressDirty && !addressValid}
                  onChange={event => {
                    setDestination({ ...destination, address: event.target.value });
                    setAddressDirty(true);
                  }}
                />
                <Input
                  label={t("Subject")}
                  value={destination.subject}
                  onChange={event => setDestination({ ...destination, subject: event.target.value })}
                />
              </>
            )}
          </div>
        </Sheet>
      </div>
    </Portal>
  );
}
